import { API_BASE_URL } from '../config/api';

// AbortController utility class to manage multiple requests
export class AbortControllerManager {
  constructor() {
    this.controllers = new Map();
  }

  // Create a new abort controller for a specific key
  createController(key) {
    // Abort existing controller for this key if it exists
    if (this.controllers.has(key)) {
      this.controllers.get(key).abort();
    }
    
    const controller = new AbortController();
    this.controllers.set(key, controller);
    return controller;
  }

  // Get signal for a specific key
  getSignal(key) {
    const controller = this.controllers.get(key);
    return controller ? controller.signal : null;
  }

  // Abort a specific request by key
  abort(key) {
    if (this.controllers.has(key)) {
      this.controllers.get(key).abort();
      this.controllers.delete(key);
    }
  }

  // Abort all requests
  abortAll() {
    this.controllers.forEach((controller) => {
      controller.abort();
    });
    this.controllers.clear();
  }

  // Remove a controller without aborting
  remove(key) {
    this.controllers.delete(key);
  }
}

// Helper function to create an abort controller
export const createAbortController = () => {
  return new AbortController();
};

const checkResponse = (response) => {
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    throw new Error('Network response was not ok');
  }
  return response;
};

export const getCasinoGameDetails = async (type, signal = null) => {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('staffToken') || localStorage.getItem('ownerToken');
    const response = await fetch(`${API_BASE_URL}/casino/casino/all-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ 
        type: type,        
        data: {}           
      }),
      signal: signal || undefined
    });

    checkResponse(response);

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Request cancelled:', error.message);
      return null;
    }
    console.error('Error fetching casino game details:', error);
    throw error;
  }
};

export const getMatchExposure = async (userId, matchId, signal = null) => {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('staffToken') || localStorage.getItem('ownerToken');
    const response = await fetch(`${API_BASE_URL}/user/matchexposures/match`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        user_id: userId,
        match_id: matchId
      }),
      signal: signal || undefined
    });

    checkResponse(response);
    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Request cancelled:', error.message);
      return null;
    }
    console.error('Error fetching match exposure:', error);
    throw error;
  }
};

export const getLastResults = async (type, signal = null) => {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('staffToken') || localStorage.getItem('ownerToken');
    const response = await fetch(`${API_BASE_URL}/casino/casino/last-results`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ type }),
      signal: signal || undefined
    });

    checkResponse(response);
    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Request cancelled:', error.message);
      return null;
    }
    console.error('Error fetching last results:', error);
    throw error;
  }
};

export const getDetailResults = async (type, mid, signal = null) => {
  try {
    const token = localStorage.getItem('token') || localStorage.getItem('staffToken') || localStorage.getItem('ownerToken');
    const response = await fetch(`${API_BASE_URL}/casino/casino/detail-results`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ type, mid }),
      signal: signal || undefined
    });

    checkResponse(response);
    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('Request cancelled:', error.message);
      return null;
    }
    console.error('Error fetching detail results:', error);
    throw error;
  }
};

